import styled from 'styled-components';
import { Container } from '@/components/common/Container';
import { Section } from '@/components/common/Section';
import { theme } from '@/styles/theme';

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;

  h1 {
    margin-bottom: ${theme.spacing.xl};
  }

  h2 {
    margin-top: ${theme.spacing.xxl};
    margin-bottom: ${theme.spacing.lg};
    color: ${theme.colors.primary.main};
  }

  h3 {
    margin-top: ${theme.spacing.xl};
    margin-bottom: ${theme.spacing.md};
  }

  p, li {
    color: ${theme.colors.text.darkGray};
    line-height: 1.8;
  }

  ul {
    list-style: disc;
    margin-left: ${theme.spacing.xl};
    margin-bottom: ${theme.spacing.lg};
  }

  li {
    margin-bottom: ${theme.spacing.sm};
  }

  strong {
    color: ${theme.colors.text.black};
    font-weight: ${theme.fontWeights.semibold};
  }

  .last-updated {
    font-size: ${theme.fontSizes.sm};
    color: ${theme.colors.text.gray};
    margin-bottom: ${theme.spacing.xxl};
  }

  .contact-email {
    color: ${theme.colors.primary.main};
    text-decoration: underline;
  }
`;

const TermsOfUse = () => {
  return (
    <Section $padding="large">
      <Container>
        <Content>
          <h1>Terms of Use</h1>
          <p className="last-updated">Last Updated: {new Date().toLocaleDateString()}</p>

          <p>
            Welcome to Lexifox! These Terms of Use ("Terms") govern your access to and use of the Lexifox
            mobile application and website (collectively, the "Service"). By using Lexifox, you agree to be
            bound by these Terms.
          </p>

          <h2>1. Acceptance of Terms</h2>

          <p>
            By creating an account or using Lexifox, you confirm that you:
          </p>
          <ul>
            <li>Are at least 13 years old</li>
            <li>Have read and understood these Terms</li>
            <li>Agree to comply with these Terms and all applicable laws</li>
          </ul>

          <p>
            If you do not agree to these Terms, you may not use the Service. If you are under 18, you
            represent that you have your parent or guardian's permission to use the Service.
          </p>

          <h2>2. Description of Service</h2>

          <p>
            Lexifox is a vocabulary learning application that helps you expand your vocabulary through:
          </p>
          <ul>
            <li>Spaced repetition learning system</li>
            <li>Swipe-based word practice</li>
            <li>Progress tracking and statistics</li>
            <li>Customizable themes and learning preferences</li>
            <li>Multi-language support (English, French, German, Italian, Spanish)</li>
          </ul>

          <p>
            We reserve the right to modify, suspend, or discontinue any part of the Service at any time,
            with or without notice.
          </p>

          <h2>3. User Accounts</h2>

          <h3>3.1 Account Creation</h3>
          <p>To access certain features, you must create an account. You agree to:</p>
          <ul>
            <li>Provide accurate, current, and complete information</li>
            <li>Maintain and update your information to keep it accurate</li>
            <li>Keep your password secure and confidential</li>
            <li>Notify us immediately of any unauthorized access</li>
            <li>Accept responsibility for all activities under your account</li>
          </ul>

          <h3>3.2 Account Termination</h3>
          <p>
            You may delete your account at any time through the app settings. We may suspend or terminate
            your account if you violate these Terms or engage in fraudulent, abusive, or illegal activities.
          </p>

          <h2>4. User Conduct</h2>

          <p>You agree NOT to:</p>
          <ul>
            <li>Use the Service for any illegal purpose or in violation of any laws</li>
            <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
            <li>Reverse engineer, decompile, or disassemble the app</li>
            <li>Use automated scripts, bots, or scrapers to access the Service</li>
            <li>Upload malicious code, viruses, or harmful content</li>
            <li>Interfere with or disrupt the Service or servers</li>
            <li>Create multiple accounts to abuse free trials or features</li>
            <li>Share your account credentials with others</li>
            <li>Use the Service to send spam or unsolicited communications</li>
          </ul>

          <h2>5. Intellectual Property</h2>

          <h3>5.1 Our Content</h3>
          <p>
            All content provided through Lexifox, including but not limited to vocabulary data, definitions,
            example sentences, images, videos, software, and the Lexifox brand, is owned by Lexifox or its
            licensors and is protected by copyright, trademark, and other intellectual property laws.
          </p>

          <h3>5.2 Your License</h3>
          <p>
            Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to:
          </p>
          <ul>
            <li>Download and use the Lexifox app on your personal device</li>
            <li>Access and use the Service for personal, non-commercial purposes</li>
          </ul>

          <h3>5.3 Restrictions</h3>
          <p>You may NOT:</p>
          <ul>
            <li>Copy, modify, distribute, sell, or lease any part of the Service</li>
            <li>Extract vocabulary data for commercial purposes</li>
            <li>Remove copyright or proprietary notices</li>
            <li>Use our trademarks without prior written permission</li>
          </ul>

          <h2>6. Premium Features and Payments</h2>

          <h3>6.1 Free and Premium Features</h3>
          <p>
            Lexifox offers both free and premium features. Premium features may include additional themes,
            advanced statistics, and other enhanced functionality.
          </p>

          <h3>6.2 Pricing and Billing</h3>
          <p>
            Premium features are available through in-app purchase via the Apple App Store or Google Play Store.
            Pricing is displayed in the app before purchase. All payments are processed by the respective app store.
          </p>

          <h3>6.3 Subscriptions</h3>
          <p>If you purchase a subscription:</p>
          <ul>
            <li>It will automatically renew unless cancelled at least 24 hours before the end of the current period</li>
            <li>You will be charged for renewal within 24 hours before the current period ends</li>
            <li>You can manage and cancel subscriptions through your App Store or Google Play account settings</li>
          </ul>

          <h3>6.4 Refunds</h3>
          <p>
            Refunds are handled according to the App Store or Google Play Store policies. We do not directly
            process refunds. To request a refund, contact Apple or Google support.
          </p>

          <h2>7. User-Generated Content</h2>

          <p>
            Currently, Lexifox does not support user-generated content beyond your personal learning data.
            If we introduce such features in the future, additional terms will apply.
          </p>

          <h2>8. Privacy</h2>

          <p>
            Your privacy is important to us. Please review our{' '}
            <a href="/privacy" className="contact-email">Privacy Policy</a> to understand how we collect,
            use, and protect your information. By using Lexifox, you agree to our privacy practices.
          </p>

          <h2>9. Disclaimers and Limitations of Liability</h2>

          <h3>9.1 Service "As Is"</h3>
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED,
            INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR
            NON-INFRINGEMENT.
          </p>

          <h3>9.2 No Guarantee of Results</h3>
          <p>
            While Lexifox is designed to help you learn vocabulary, we do not guarantee specific learning
            outcomes or results. Your progress depends on your individual effort and practice.
          </p>

          <h3>9.3 Limitation of Liability</h3>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, LEXIFOX SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
            SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED
            DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
          </p>

          <p>
            OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATED TO THE SERVICE SHALL NOT
            EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM, OR $100, WHICHEVER IS GREATER.
          </p>

          <h2>10. Indemnification</h2>

          <p>
            You agree to indemnify, defend, and hold harmless Lexifox, its officers, directors, employees, and
            agents from any claims, liabilities, damages, losses, and expenses (including legal fees) arising
            out of or related to your:
          </p>
          <ul>
            <li>Use of the Service</li>
            <li>Violation of these Terms</li>
            <li>Violation of any rights of another party</li>
            <li>Violation of any applicable laws</li>
          </ul>

          <h2>11. Third-Party Services</h2>

          <p>
            The Service may contain links to third-party websites or services that are not owned or controlled
            by Lexifox. We have no control over and assume no responsibility for the content, privacy policies,
            or practices of any third-party services.
          </p>

          <h2>12. Modifications to Terms</h2>

          <p>
            We reserve the right to modify these Terms at any time. If we make material changes, we will:
          </p>
          <ul>
            <li>Post the updated Terms on this page</li>
            <li>Update the "Last Updated" date</li>
            <li>Notify you via email or in-app notification</li>
          </ul>

          <p>
            Your continued use of the Service after changes become effective constitutes your acceptance of
            the new Terms. If you do not agree to the modified Terms, you must stop using the Service.
          </p>

          <h2>13. Termination</h2>

          <p>
            We reserve the right to terminate or suspend your access to the Service immediately, without prior
            notice or liability, for any reason, including if you breach these Terms.
          </p>

          <p>
            Upon termination, your right to use the Service will cease immediately. All provisions of these
            Terms that by their nature should survive termination shall survive, including ownership provisions,
            warranty disclaimers, and limitations of liability.
          </p>

          <h2>14. Dispute Resolution</h2>

          <h3>14.1 Governing Law</h3>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of {/* TODO: Add your jurisdiction */},
            without regard to its conflict of law provisions.
          </p>

          <h3>14.2 Arbitration</h3>
          <p>
            {/* TODO: Add arbitration clause if applicable to your jurisdiction */}
            Any disputes arising out of or related to these Terms or the Service shall be resolved through
            binding arbitration, except that either party may seek injunctive relief in court.
          </p>

          <h2>15. Miscellaneous</h2>

          <h3>15.1 Entire Agreement</h3>
          <p>
            These Terms constitute the entire agreement between you and Lexifox regarding the Service and
            supersede all prior agreements.
          </p>

          <h3>15.2 Severability</h3>
          <p>
            If any provision of these Terms is found to be unenforceable, the remaining provisions will
            remain in full force and effect.
          </p>

          <h3>15.3 Waiver</h3>
          <p>
            Our failure to enforce any right or provision of these Terms will not be considered a waiver
            of those rights.
          </p>

          <h3>15.4 Assignment</h3>
          <p>
            You may not assign or transfer these Terms without our prior written consent. We may assign
            our rights and obligations without restriction.
          </p>

          <h2>16. Contact Information</h2>

          <p>
            If you have questions about these Terms, please contact us:
          </p>

          <p>
            <strong>Email:</strong>{' '}
            <a href="mailto:legal@lexifox.com" className="contact-email">
              legal@lexifox.com
            </a>
            <br />
            {/* TODO: Add physical address and company registration details if required */}
          </p>

      
        </Content>
      </Container>
    </Section>
  );
};

export default TermsOfUse;
